/**
 * Site Feedback loader: the script SPARK puts on a client's website.
 *
 *   <script src="https://<dash>/site-feedback/v1/loader.js" data-site-key="<key>" async></script>
 *
 * Ordinary visitors on a "links only" engagement cost nothing beyond this
 * small script: no hooks are installed, and after one config check the "off"
 * answer is cached for an hour. A reviewer who opens an activation link
 * (https://site/#spark-feedback=<token>) gets the launcher on every page for
 * 30 days on that browser.
 *
 * SPARK Site Tools (WordPress) prints the same tag and, for a signed-in
 * administrator, adds data-activation-token (a short-lived signed Internal QA
 * token), data-reporter-name, and data-reporter-url (a same-origin address
 * that answers with the admin's name and email). That token ranks below any
 * link the browser opened, is never stored, and is silenced by "Stop showing"
 * until #spark-feedback=reset (the plugin's "Show Feedback button" toolbar link).
 */

import { installHooks, capturedContext } from "./hooks";
import { mountLauncher, type Launcher } from "./launcher";
import { readActivation, readCachedConfig, writeCachedConfig, clearActivation, isOptedOut, optOut } from "./storage";
import { ADMIN_ACTIVATION_RE } from "@/lib/site-feedback/shared/vocabulary";
import { WIDGET_VERSION, type EditorContext, type WidgetConfig } from "../runtime";

const BUILDER_PREVIEW_PARAMS = ["elementor-preview", "customize_changeset_uuid", "et_fb", "bricks", "fl_builder", "ct_builder", "vc_editable", "brizy-edit-iframe"];

function isBuilderPreview(): boolean {
  try {
    const params = new URLSearchParams(location.search);
    return BUILDER_PREVIEW_PARAMS.some((p) => params.has(p)) || document.documentElement.classList.contains("elementor-editor-active");
  } catch {
    return false;
  }
}

type Chunk = "editor" | "recorder";

/** What a chunk name read from manifest.json must look like before the Trusted Types policy allows it. */
const CHUNK_FILE_RE = /^(editor|recorder)\.[0-9a-f]{12}\.js$/;

interface TrustedTypePolicyLike {
  createScriptURL(url: string): unknown;
}

/**
 * Sites enforcing Trusted Types reject a plain string assigned to script.src.
 * One policy, created once and allowing exactly our own chunk URLs, keeps the
 * widget working there without loosening anything else. `allowed` is read on
 * every call, so a current name found in manifest.json after a deploy can join
 * it (see loadChunk). Once, because a second createPolicy with the same name
 * throws on sites that list allowed policy names, which used to break a retry
 * after a failed editor load. (Such a site must add "spark-feedback" to its
 * list; see the install docs.)
 */
function chunkUrlPolicy(allowed: ReadonlySet<string>): (url: string) => string {
  let policy: TrustedTypePolicyLike | null | undefined;
  return (url) => {
    const tt = (window as unknown as { trustedTypes?: { createPolicy(name: string, rules: { createScriptURL(u: string): string }): TrustedTypePolicyLike } }).trustedTypes;
    if (!tt) return url;
    if (policy === undefined) {
      try {
        policy = tt.createPolicy("spark-feedback", {
          createScriptURL: (candidate: string) => {
            if (!allowed.has(candidate)) throw new TypeError("spark-feedback policy only loads Site Feedback chunks");
            return candidate;
          },
        });
      } catch {
        policy = null;
      }
    }
    return policy ? (policy.createScriptURL(url) as string) : url;
  };
}

/** An address on this page's own origin, or null (the lookup sends this site's cookies). */
function sameOriginUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, location.href);
    return url.origin === location.origin ? url.href : null;
  } catch {
    return null;
  }
}

function currentScript(): HTMLScriptElement | null {
  const script = document.currentScript;
  if (script instanceof HTMLScriptElement && script.dataset.siteKey) return script;
  return document.querySelector<HTMLScriptElement>('script[data-site-key][src*="/site-feedback/"]');
}

function main(): void {
  if (window.SparkFeedback) return; // installed twice (plugin + snippet): first one wins
  window.SparkFeedback = { version: WIDGET_VERSION };
  try {
    if (window.top !== window.self) return; // never inside iframes or builder canvases
  } catch {
    return;
  }
  if (isBuilderPreview()) return;

  const script = currentScript();
  const siteKey = script?.dataset.siteKey ?? "";
  if (!script || !/^[0-9a-f]{32}$/.test(siteKey)) return;
  const scriptUrl = new URL(script.src, location.href);
  const dashOrigin = scriptUrl.origin;
  const assetBase = scriptUrl.href.slice(0, scriptUrl.href.lastIndexOf("/") + 1);
  const nonce = script.nonce || undefined;
  // Keep a reference to the untouched fetch for our own calls.
  const nativeFetch = window.fetch.bind(window);

  const linkToken = readActivation(siteKey);
  const attrToken = script.dataset.activationToken ?? "";
  const adminToken = !linkToken && ADMIN_ACTIVATION_RE.test(attrToken) && !isOptedOut(siteKey) ? attrToken : null;
  const token = linkToken ?? adminToken;
  const activationSource: EditorContext["activationSource"] = linkToken ? "link" : adminToken ? "admin" : "public";
  const reporterName = (script.dataset.reporterName ?? "").replace(/\s+/g, " ").trim().slice(0, 120);
  const reporterLookup = adminToken ? sameOriginUrl(script.dataset.reporterUrl) : null;
  const cached = readCachedConfig(siteKey, token);
  if (cached?.kind === "off") return;
  if (token || cached?.kind === "on") installHooks(dashOrigin);

  let launcher: Launcher | null = null;
  const files: Record<Chunk, string> = { editor: __SPARK_FEEDBACK_EDITOR_FILE__, recorder: __SPARK_FEEDBACK_RECORDER_FILE__ };
  const allowed = new Set([assetBase + files.editor, assetBase + files.recorder]);
  const trusted = chunkUrlPolicy(allowed);
  const loading = new Map<Chunk, Promise<void>>();

  const inject = (file: string) =>
    new Promise<void>((resolve, reject) => {
      const tag = document.createElement("script");
      tag.src = trusted(assetBase + file);
      tag.async = true;
      if (nonce) tag.nonce = nonce;
      tag.onload = () => resolve();
      tag.onerror = () => reject(new Error(`${file} failed to load`));
      document.head.append(tag);
    });

  /**
   * The chunk's name in manifest.json, when it differs from ours. The names are
   * baked in at build time and each deploy deletes the previous build's files,
   * so a loader still cached from before a deploy (max-age 300, then a day of
   * stale-while-revalidate) asks for a file that is gone. Only a name shaped
   * like our own chunks comes back: it is about to pass the Trusted Types policy.
   */
  const currentFile = (chunk: Chunk): Promise<string | null> =>
    nativeFetch(`${assetBase}manifest.json`, { credentials: "omit", mode: "cors", cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((manifest: Partial<Record<Chunk, unknown>> | null) => {
        const file = manifest?.[chunk];
        return typeof file === "string" && CHUNK_FILE_RE.exec(file)?.[1] === chunk && file !== files[chunk] ? file : null;
      })
      .catch(() => null);

  /**
   * Load one of our chunks once; a failed load can be retried, a loaded one is
   * reused. A load that fails is tried once more under the manifest's name.
   */
  const loadChunk = (chunk: Chunk, ready: () => boolean): Promise<void> => {
    if (ready()) return Promise.resolve();
    let pending = loading.get(chunk);
    if (!pending) {
      pending = inject(files[chunk])
        .catch(async (err: unknown) => {
          const file = await currentFile(chunk);
          if (!file) throw err;
          allowed.add(assetBase + file);
          files[chunk] = file;
          return inject(file);
        })
        .catch((err: unknown) => {
          loading.delete(chunk);
          throw err;
        })
        .then(() => {
          if (!ready()) throw new Error(`${files[chunk]} did not register`);
        });
      loading.set(chunk, pending);
    }
    return pending;
  };

  // Session replay: testers only (never a public visitor), only where an
  // engagement records replays, and only in browsers that can gzip it.
  let recording = false;
  const recorder = () => window.SparkFeedback?.recorder;
  const syncRecorder = (config: WidgetConfig | null) => {
    const wanted = Boolean(config) && activationSource !== "public" && typeof CompressionStream === "function" && config!.engagements.some((e) => e.replay);
    if (wanted && !recording) {
      recording = true;
      loadChunk("recorder", () => Boolean(recorder()))
        .then(() => recording && recorder()?.start())
        .catch(() => (recording = false));
    } else if (!wanted && recording) {
      recording = false;
      recorder()?.stop();
    }
  };
  const resumeRecording = () => recording && recorder()?.resume();

  const show = (config: WidgetConfig) => {
    syncRecorder(config);
    if (launcher) return;
    installHooks(dashOrigin);
    const instance = mountLauncher(config, nonce, () => {
      instance.setBusy(true);
      // Frozen at the click, before anything else happens on the page.
      const replay = recording ? (recorder()?.freeze() ?? null) : null;
      loadChunk("editor", () => Boolean(window.SparkFeedback?.openEditor))
        .then(() => {
          const context: EditorContext = {
            config,
            siteKey,
            dashOrigin,
            root: instance.root,
            nonce,
            captured: capturedContext,
            setLauncherVisible: (visible) => instance.setVisible(visible),
            onClose: () => {
              instance.setVisible(true);
              instance.setBusy(false);
              resumeRecording();
            },
            deactivate: () => {
              optOut(siteKey);
              writeCachedConfig(siteKey, token, null);
              syncRecorder(null);
              instance.destroy();
              launcher = null;
            },
            activationSource,
            reporterDefaults: reporterName || reporterLookup ? { name: reporterName || undefined, lookupUrl: reporterLookup ?? undefined } : undefined,
            fetch: nativeFetch,
            replay,
          };
          instance.setVisible(false);
          window.SparkFeedback?.openEditor?.(context, "screen");
        })
        .catch((err: unknown) => {
          instance.setBusy(false);
          resumeRecording();
          console.warn("[SPARK Site Feedback] could not open the editor", err);
        });
    });
    launcher = instance;
  };

  if (cached?.kind === "on") show(cached.config);

  const url = new URL(`${dashOrigin}/api/site-feedback/public/config`);
  url.searchParams.set("key", siteKey);
  if (token) url.searchParams.set("t", token);
  nativeFetch(url.href, { credentials: "omit", mode: "cors", cache: "no-store" })
    .then((res) => {
      // Only an explicit answer counts as "off"; an outage must not wipe a
      // reviewer's activation or cache a false negative.
      if (res.status >= 500 || res.status === 429) throw new Error(`config ${res.status}`);
      return res.ok ? res.json() : { enabled: false };
    })
    .then((body: { enabled?: boolean } & Partial<WidgetConfig>) => {
      if (body.enabled && Array.isArray(body.engagements) && body.engagements.length > 0) {
        const config = body as WidgetConfig;
        writeCachedConfig(siteKey, token, config);
        show(config);
      } else {
        writeCachedConfig(siteKey, token, null);
        syncRecorder(null);
        // A link token that no longer works (link regenerated, engagement
        // closed): forget it so this browser stops checking. An admin token is
        // re-minted by the plugin every hour, so a stale one is simply ignored;
        // treating it like a dead link would hide the button for good.
        if (linkToken) clearActivation(siteKey);
        launcher?.destroy();
        launcher = null;
      }
    })
    .catch(() => {
      /* Dash unreachable: keep any cached launcher, try again next page */
    });
}

main();
